import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, toDisplayString as _toDisplayString, withCtx as _withCtx, renderList as _renderList, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-file-id", "draggable"]
const _hoisted_2 = { class: "file-item__string" }
const _hoisted_3 = { class: "file-item__name" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = {
  key: 1,
  class: "file-item__percent"
}
const _hoisted_6 = { class: "file-item__percent-text" }
const _hoisted_7 = { class: "file-item__jobs-list" }
const _hoisted_8 = {
  key: 0,
  class: "file-item__jobs-list-divider"
}

import {
  ref,
  computed,
  inject,
  Ref,
  nextTick,
  watch,
  onMounted,
  onUnmounted,
} from "vue";
import AppHashtag from "@/components/ui-kit/hashtag/AppHashtag.vue";
import FileInput from "./FileInput.vue";
import {
  DragGeneralState,
  FileOut,
  FolderOut,
} from "@/models/interfaces/file/file-model";
import AppIcon from "../ui-kit/icon/AppIcon.vue";
import { typeIconMap, getFileTypeFromString } from "@/utils/fileIcons";
import AppTooltip from "../ui-kit/tooltip/AppTooltip.vue";
import AppCheckbox from "../ui-kit/checkbox/AppCheckbox.vue";
import FileMenu from "./FileMenu.vue";
import FileDropdown from "./FileDropdown.vue";
import AppResizableDropdown from "../ui-kit/dropdown/AppResizableDropdown.vue";
import FileJobElement from "./FileJobElement.vue";
import { navigateToJob } from "./jobNavigation";
import { JobsApi } from "@/ api/JobsApi";
import { Job, JobInfo } from "@/store/jobs";
import { clientPageStore } from "@/store/client-page";
import { projectPageStore } from "@/store/project-page";
import { useRoute, useRouter } from "vue-router";
import { ProjectContainer } from "@/models/interfaces/project/project-models";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { escapeHtml, highlightFileSearchText } from "@/utils/textHelpers";
import { i18n } from "@/i18n";
import { useBreakpoints } from "@/composables/useBreakpoints";

interface Props {
  file: FileOut;
  level?: number;
  parentNode?: FolderOut;
  fileType?: "default";
  isUploading?: boolean;
  ruleAccess: number;
}

interface ChildEventData {
  type: string;
  data: any;
}

type FileTag = FileOut["Tags"][number];

type DragEntity =
  | { type: "folder"; folder: FolderOut }
  | { type: "file"; file: FileOut; parentFolder: FolderOut }
  | {
      type: "selection";
      folders: FolderOut[];
      files: { file: FileOut; parentFolder: FolderOut }[];
    };


export default /*@__PURE__*/_defineComponent({
  __name: 'FileString',
  props: {
    file: {},
    level: { default: 0 },
    parentNode: {},
    fileType: { default: "default" },
    isUploading: { type: Boolean, default: false },
    ruleAccess: { default: 0 }
  },
  emits: ["select", "selectFileAction", "rename", "replace"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const { isMobile } = useBreakpoints();

const siblingFileNames = computed(() =>
  (props.parentNode?.Files ?? [])
    .filter((item) => item.Id !== props.file.Id)
    .map((item) => item.Name),
);

const clientStore = clientPageStore();
const projectStore = projectPageStore();
const route = useRoute();
const router = useRouter();
const boundJobs = ref<JobInfo[]>([]);
const boundJobsLoading = ref<Promise<void> | null>(null);
const jobDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(
  null,
);

const showJobIcon = computed(
  () =>
    props.file.JobBindings?.length > 0 &&
    (clientStore.client?.JobAccess !== 0 ||
      projectStore.project?.JobAccess !== 0),
);

const loadBoundJobs = async () => {
  if (boundJobs.value.length || boundJobsLoading.value) {
    return boundJobsLoading.value;
  }

  boundJobsLoading.value = Promise.all(
    props.file.JobBindings.map((binding) => JobsApi.getJobInfo(binding.JobId)),
  )
    .then((jobs) => {
      boundJobs.value = jobs;
    })
    .finally(() => {
      boundJobsLoading.value = null;
    });

  return boundJobsLoading.value;
};

const goToJob = (job: Job) => {
  resetJobNavigationState();
  navigateToJob(job, route, router);
};

const goToSingleJob = async () => {
  await loadBoundJobs();
  const job = boundJobs.value[0]?.Job;
  if (job) goToJob(job);
};

const fileItemRef = ref<HTMLElement | null>(null);
const isMultiline = ref(false);
let fileResizeObserver: ResizeObserver | null = null;
const emit = __emit;

let isEditable = ref(false);
let isNameEditable = ref(false);
let levelStyle = computed(() => {
  return {
    paddingLeft: `${props.level == 0 ? 2 : props.level * 16}px`,
  };
});
const isFlashing = ref(false);

const menuElementsFile = computed(() => {
  if ((props.ruleAccess == 2 && props.file.Editable) || isAdmin) {
    return [
      {
        name: String(i18n.global.t("file_rename")),
        nameIcon: "edit-pencil",
        event: "renameFile",
      },
      { name: String(i18n.global.t("file_download")), nameIcon: "download", event: "downloadFile" },
      {
        name: String(i18n.global.t("file_view_information")),
        nameIcon: "info-circle-outline",
        event: "infoFile",
      },
      { name: String(i18n.global.t("remove")), nameIcon: "delete-outline", event: "removeFile" },
    ];
  } else if (props.ruleAccess !== 2 && props.file.Editable) {
    return [
      { name: String(i18n.global.t("file_download")), nameIcon: "download", event: "downloadFile" },
      {
        name: String(i18n.global.t("file_view_information")),
        nameIcon: "info-circle-outline",
        event: "infoFile",
      },
    ];
  }

  return [
    {
      name: String(i18n.global.t("file_view_information")),
      nameIcon: "info-circle-outline",
      event: "infoFile",
    },
  ];
});

const dragIconName = computed(() => {
  return (
    typeIconMap[getFileTypeFromString(props.file.Name)] || "file-universal"
  );
});

const searchMode = inject<Ref<boolean>>("searchMode");
const percentUploading = inject<boolean>("percentUploading");
const searchText = inject<Ref<string>>("searchText");
const isAdmin = inject("isAdmin");
const dirFocusId = inject<Ref<string>>("dirFocusId");
const renameFileFocus = inject<Ref<boolean>>("renameFileFocus", ref(false));
const disableRenameFileFocus = inject<Function>("disableRenameFileFocus");
const changeElFocusId = inject<Function>("changeElFocusId");
const flashFileId = inject<Ref<number | null>>("flashFileId", ref(null));
const dropdownMenu = ref<InstanceType<typeof FileDropdown> | null>(null);
const suppressNextFileClick = ref(false);

const emitToParent =
  inject<(eventData: ChildEventData) => boolean | Promise<boolean>>(
    "childEventEmitter",
  );

const containerProject = inject<Ref<ProjectContainer>>("containerProject");

const changeModalPos = inject<Function>("changeModalPos");

const menuAnchorRef = ref<HTMLElement | null>(null);

const setModalPositionNearDropdown = () => {
  const el = menuAnchorRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();

  changeModalPos?.({
    x: rect.right + window.scrollX + 8,
    y: rect.top + window.scrollY,
  });
};

const onJobsClosed = () => {
  if (!fileItemRef.value?.matches(":hover")) {
    isEditable.value = false;
    dropdownMenu.value?.closeDropdown();
  }
};

const resetJobNavigationState = () => {
  jobDropdown.value?.close();
  dropdownMenu.value?.closeDropdown();
  isEditable.value = false;
  stopGlobalHoverWatch();
};

const isSelectionInsideInput = () => {
  const selection = window.getSelection();
  const inputEl = inputRef.value?.$el as HTMLElement | undefined;

  if (!selection || selection.isCollapsed || !inputEl) return false;

  const { anchorNode, focusNode } = selection;

  return (
    !!anchorNode &&
    !!focusNode &&
    inputEl.contains(anchorNode) &&
    inputEl.contains(focusNode)
  );
};

const handleMouseDownCapture = () => {
  suppressNextFileClick.value = isSelectionInsideInput();
};

const highlightedName = computed(() => {
  const escapedName = escapeHtml(props.file.Name);
  if (!searchMode?.value || !searchText?.value) return escapedName;

  return highlightFileSearchText(searchText.value, escapedName);
});

const selectedTags = ref<TagWithFrequency[]>([]);
const isTagsUpdating = ref(false);

const mapFileTagToSelectorTag = (tag: FileTag): TagWithFrequency => ({
  ...tag,
  Frequency: 0,
  FullName: tag.Name,
  Type: TagBaseType.Common,
});

const mapSelectorTagToFileTag = (tag: TagWithFrequency): FileTag => ({
  Signed: false,
  SignedBy: "",
  SignedAt: null as unknown as Date,
  Id: tag.Id,
  Name: tag.Name,
});

const isSameTag = (
  a: { Id: any; Name: string },
  b: { Id: any; Name: string },
): boolean => {
  const aIdExists = a.Id !== null && a.Id !== undefined;
  const bIdExists = b.Id !== null && b.Id !== undefined;

  if (aIdExists && bIdExists) {
    return String(a.Id) === String(b.Id);
  }

  return a.Name.trim().toLowerCase() === b.Name.trim().toLowerCase();
};

watch(
  () => props.file.Tags,
  (tags) => {
    selectedTags.value = (tags ?? []).map(mapFileTagToSelectorTag);
  },
  { immediate: true, deep: true },
);

const changeTags = async (newSelectedTags: TagWithFrequency[]) => {
  if (isTagsUpdating.value) return;

  const currentFileTags: FileTag[] = props.file.Tags ?? [];
  const nextSelectedTags: TagWithFrequency[] = newSelectedTags ?? [];

  const addedTags = nextSelectedTags.filter(
    (selectedTag) =>
      !currentFileTags.some((fileTag) => isSameTag(fileTag, selectedTag)),
  );

  const removedTags = currentFileTags.filter(
    (fileTag) =>
      !nextSelectedTags.some((selectedTag) => isSameTag(fileTag, selectedTag)),
  );

  if (addedTags.length === 0 && removedTags.length === 0) {
    return;
  }

  isTagsUpdating.value = true;

  try {
    const success = await emitToParent?.({
      type: "changeFileTags",
      data: {
        dir: props.parentNode,
        file: props.file,
        tags: nextSelectedTags.map(mapSelectorTagToFileTag),
        addedTags,
        removedTags,
      },
    });

    if (!success) {
      selectedTags.value = currentFileTags.map(mapFileTagToSelectorTag);
    }
  } catch (error) {
    selectedTags.value = currentFileTags.map(mapFileTagToSelectorTag);
  } finally {
    isTagsUpdating.value = false;
  }
};

const onFileTagCreated = async (tags: FileTag[]) => {
  selectedTags.value = (tags ?? []).map(mapFileTagToSelectorTag);

  await emitToParent?.({
    type: "setFileTags",
    data: {
      file: props.file,
      tags,
    },
  });
};

const removeTag = async (item: { Id: any; Name: string }) => {
  if (isTagsUpdating.value) return;

  const nextSelectedTags = (selectedTags.value ?? []).filter(
    (tag) => !isSameTag(tag, item),
  );

  await changeTags(nextSelectedTags);
};

const abortUpload = () => {
  emitToParent?.({
    type: "abort",
    data: {
      file: props.file,
    },
  });
};

const advancedMode = inject("mode");

const inputRef = ref<InstanceType<typeof FileInput> | null>(null);

const selectFile = (value: boolean) => {
  emit("select", props.file, value);
};

const deselectFile = () => {
  emit("select", props.file, false);
};

const renameFile = (value: boolean) => {
  if (changeElFocusId) {
    changeElFocusId(props.file.Id);
  }

  emit("rename", props.file, value);
};

const handleFileItemClick = (event: MouseEvent) => {
  if (
    (containerProject?.value.FileAccess == 0 && !isAdmin) ||
    !props.file.Editable
  )
    return;
  if (advancedMode.value || props.isUploading == true) return;

  if (isNameEditable.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  if (!window.getSelection()?.isCollapsed) {
    return;
  }
  
  if (isMobile.value) return;
  
  setModalPositionNearDropdown();
  chooseAction("downloadFileClick");
};

const chooseAction = (option: string) => {
  dropdownMenu.value?.closeDropdown();

  if (option == "renameFile") {
    editName();
    return;
  }

  if (option == "downloadFileClick") {
    emit("selectFileAction", props.file, "downloadFile");
    return;
  }

  emit("selectFileAction", props.file, option);
};

const editName = async () => {
  isNameEditable.value = true;

  await nextTick();

  inputRef.value?.focus();
};

const blurInput = () => {
  isNameEditable.value = false;
};

const finishEditingOnOutsideTap = (event: PointerEvent) => {
  if (!isMobile.value || !isNameEditable.value) return;

  const inputElement = inputRef.value?.$el as HTMLElement | undefined;
  if (inputElement?.contains(event.target as Node)) return;

  inputRef.value?.finishEditing();
};

watch(isNameEditable, (editing) => {
  if (editing) {
    document.addEventListener("pointerdown", finishEditingOnOutsideTap, true);
  } else {
    document.removeEventListener("pointerdown", finishEditingOnOutsideTap, true);
  }
});

let closeMenuTimer: ReturnType<typeof setTimeout> | null = null;

const startGlobalHoverWatch = () => {
  document.addEventListener("mousemove", checkHoverState);
};

const stopGlobalHoverWatch = () => {
  document.removeEventListener("mousemove", checkHoverState);
};

const checkHoverState = () => {
  const overFile = fileItemRef.value?.matches(":hover");
  const overDropdownContent = !!document.querySelector(
    ".dropdown-content:hover",
  );
  const overFileDropdown = !!dropdownMenu.value?.$el?.matches(":hover");

  if (!overFile && !overDropdownContent && !overFileDropdown) {
    isEditable.value = false;
    dropdownMenu.value?.closeDropdown();
    stopGlobalHoverWatch();
  }
};

const handleMouseEnter = () => {
  if (isMobile.value) return;

  isEditable.value = true;
  startGlobalHoverWatch();
};

const handleMouseLeave = (event: MouseEvent) => {
  if (isMobile.value) return;

  const relatedTarget = event.relatedTarget as HTMLElement | null;
  if (relatedTarget?.closest(".dropdown-content")) return;

  isEditable.value = false;
  dropdownMenu.value?.closeDropdown();
  stopGlobalHoverWatch();
};

onMounted(() => {
  fileResizeObserver = new ResizeObserver(([entry]) => {
    const content =
      entry.target.querySelector<HTMLElement>(".file-item__string");
    isMultiline.value = (content?.getBoundingClientRect().height ?? 0) > 20;
  });

  if (fileItemRef.value) {
    fileResizeObserver.observe(fileItemRef.value);
  }
});

onUnmounted(() => {
  if (closeMenuTimer) {
    clearTimeout(closeMenuTimer);
  }

  fileResizeObserver?.disconnect();
});

onUnmounted(() => {
  stopGlobalHoverWatch();
  document.removeEventListener("pointerdown", finishEditingOnOutsideTap, true);
});

watch(
  () => flashFileId?.value,
  async (newId) => {
    if (newId === props.file.Id) {
      await nextTick();

      if (fileItemRef.value) {
        fileItemRef.value.style.animation = "none";
        fileItemRef.value.offsetHeight;
        fileItemRef.value.style.animation = "";
      }

      isFlashing.value = true;

      await nextTick();
      fileItemRef.value?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setTimeout(() => {
        isFlashing.value = false;
      }, 2000);
    }
  },
);

watch(
  () => renameFileFocus.value,
  (newValue) => {
    if (newValue === true && dirFocusId?.value == props.file.Id) {
      isNameEditable.value = true;

      nextTick(() => {
        inputRef.value?.focus();
      });

      disableRenameFileFocus?.();
    }
  },
);

const dragState = inject<{
  draggedEntity: DragEntity | null;
  dropTarget: FolderOut | null;
  isValidDrop: boolean;
}>("dragState", {
  draggedEntity: null,
  dropTarget: null,
  isValidDrop: false,
});
const setDragState = inject<Function>("setDragState");

const dragGeneralState = inject<DragGeneralState>("dragGeneralState", {
  name: "",
  type: "",
  count: 0,
  active: false,
  isValid: true,
  x: 0,
  y: 0,
});

let isDragStart = ref(false);
let isDragSource = ref(false);

const getSelectedDragEntity = inject<() => DragEntity>("getSelectedDragEntity");
const isFileTouchDragPending = inject<() => boolean>(
  "isFileTouchDragPending",
  () => false,
);

const handleDragStart = (event: DragEvent) => {
  if (isFileTouchDragPending()) {
    event.preventDefault();
    return;
  }

  const sel = window.getSelection();
  if (sel && !sel.isCollapsed) {
    event.preventDefault();
    sel.removeAllRanges();
    return;
  }

  if (!advancedMode.value) {
    event.preventDefault();
    return;
  }

  if (!event.dataTransfer) return;

  const img = document.createElement("div");
  img.style.width = "0";
  img.style.height = "0";
  document.body.appendChild(img);
  event.dataTransfer.setDragImage(img, 0, 0);
  event.dataTransfer.effectAllowed = "copyMove";

  dragGeneralState.active = true;
  dragGeneralState.x = event.clientX;
  dragGeneralState.y = event.clientY;

  let draggedEntity: DragEntity = props.parentNode
    ? {
        type: "file",
        file: props.file,
        parentFolder: props.parentNode,
      }
    : {
        type: "selection",
        folders: [],
        files: [],
      };

  const selection = getSelectedDragEntity?.();
  if (selection && selection.type === "selection") {
    const hasAnySelection =
      selection.folders.length > 0 || selection.files.length > 0;

    if (hasAnySelection && props.file.isSelected === true) {
      draggedEntity = selection;
    }
  }

  const toPayload = (f: FileOut) => ({
    Id: f.Id,
    Name: f.Name,
    Signed: f.Signed,
  });
  if (draggedEntity.type === "file") {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify([toPayload(props.file)]),
    );
  } else if (draggedEntity.type === "selection") {
    const files = draggedEntity.files.map((f) => toPayload(f.file));
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify(files.length ? files : [toPayload(props.file)]),
    );
  } else {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        type: draggedEntity.type,
      }),
    );
  }

  setDragState?.({
    draggedEntity,
    draggedElement: event.target as HTMLElement,
    dropTarget: null,
    isValidDrop: false,
  });

  isDragSource.value = true;
};

function onDrag(e: DragEvent) {
  if (!e.clientX && !e.clientY) return;

  dragGeneralState.x = e.clientX;
  dragGeneralState.y = e.clientY;
}

const handleDragEnd = () => {
  setDragState?.({
    draggedEntity: null,
    draggedElement: null,
    dropTarget: null,
    isValidDrop: false,
  });

  dragGeneralState.active = false;
  isDragSource.value = false;
  isDragStart.value = false;
};

let isDragValid = ref(true);
const isExternalFileDrag = (event: DragEvent): boolean => {
  const types = Array.from(event.dataTransfer?.types ?? []);
  return types.includes("Files");
};

const handleDragOver = (event: DragEvent) => {
  const externalFileDrag = isExternalFileDrag(event);

  if (!externalFileDrag) {
    isDragValid.value = false;
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (props.ruleAccess !== 2 || !props.file.Editable) {
    isDragValid.value = false;
    event.dataTransfer!.dropEffect = "none";
    return;
  }

  isDragValid.value = true;

  const items = event.dataTransfer?.items ?? [];
  for (const item of items) {
    const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;

    if (entry && !entry.isFile) {
      isDragValid.value = false;
      break;
    }
  }

  event.dataTransfer!.dropEffect = isDragValid.value ? "move" : "none";
};

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
  if (isDragSource.value) return;
  isDragStart.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  const current = event.currentTarget as HTMLElement;
  const related = event.relatedTarget as HTMLElement | null;

  if (related && current.contains(related)) return;

  isDragValid.value = true;
  isDragStart.value = false;
};

const handleDrop = async (event: DragEvent) => {
  const externalFileDrag = Array.from(event.dataTransfer?.types ?? []).includes(
    "Files",
  );

  if (!externalFileDrag || dragState.draggedEntity) {
    isDragValid.value = false;
    isDragStart.value = false;
    return;
  }

  if (props.ruleAccess !== 2 || !props.file.Editable) {
    isDragValid.value = false;
    isDragStart.value = false;
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    isDragStart.value = false;
    return;
  }

  if (files && files.length > 0) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        if (emitToParent) {
          emitToParent({
            type: "error",
            data: {
              type: "error",
              errorType: "size",
            },
          });
        }

        isDragStart.value = false;

        return;
      }

      const fileExtension = getFileTypeFromString(file.name);
      const originalExtension = getFileTypeFromString(props.file.Name);

      if (fileExtension !== originalExtension) {
        if (emitToParent) {
          emitToParent({
            type: "error",
            data: {
              type: "error",
              errorType: "extension",
            },
          });
        }

        isDragStart.value = false;

        return;
      }

      if (file.type == "") {
        if (emitToParent) {
          emitToParent({
            type: "error",
            data: {
              type: "error",
              errorType: "folder",
            },
          });
        }

        return;
      }
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("File", file);
      emit("replace", props.file, file.name, formData);
    }

    isDragStart.value = false;

    return;
  }

  isDragStart.value = false;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["file-item", {
      'file-item_editable': _unref(isEditable),
      'file-item-download': !_unref(advancedMode),
      'file-item-dragging-valid': _unref(isDragValid) && _unref(isDragStart),
      'file-item-dragging-invalid': !_unref(isDragValid) && _unref(isDragStart),
      'file-item--flash': isFlashing.value,
      'file-item--multiline': isMultiline.value,
    }]),
    ref_key: "fileItemRef",
    ref: fileItemRef,
    "data-file-id": _ctx.file.Id,
    style: _normalizeStyle(_unref(levelStyle)),
    onMousedownCapture: handleMouseDownCapture,
    onMouseenter: handleMouseEnter,
    onMouseleave: _cache[5] || (_cache[5] = ($event: any) => (handleMouseLeave($event))),
    draggable: 
      ((props.ruleAccess === 2 && _ctx.file.Editable) || _unref(isAdmin)) &&
      _unref(advancedMode) &&
      _ctx.isUploading == false &&
      _unref(dragGeneralState).isValid &&
      _ctx.file.isSelected == true
        ? true
        : false
    ,
    onDragstart: handleDragStart,
    onDragend: handleDragEnd,
    onDragover: handleDragOver,
    onDragenter: handleDragEnter,
    onDragleave: handleDragLeave,
    onDrop: handleDrop,
    onDrag: onDrag,
    onClick: handleFileItemClick
  }, [
    (_unref(advancedMode) && _ctx.fileType == 'default' && !_ctx.isUploading)
      ? (_openBlock(), _createBlock(AppCheckbox, {
          key: 0,
          class: "file-item__checkbox",
          value: _ctx.file.isSelected,
          onInput: selectFile,
          onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
          disabled: 
        !_unref(isAdmin) &&
        (props.ruleAccess === 0 ||
          !_ctx.file.Editable)
      
        }, null, 8, ["value", "disabled"]))
      : _createCommentVNode("", true),
    _createVNode(AppIcon, {
      class: "file-item__icon",
      name: dragIconName.value,
      height: "18px",
      width: "18px",
      color: _ctx.isUploading == false ? '#979797' : '#d2d2d2',
      cursor: "pointer"
    }, null, 8, ["name", "color"]),
    _createElementVNode("div", _hoisted_2, [
      (_ctx.fileType == 'default')
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            ((props.ruleAccess === 2 && _ctx.file.Editable) || _unref(isAdmin))
              ? (_openBlock(), _createBlock(AppHashtag, {
                  key: 0,
                  ref: "hashtagRef",
                  class: _normalizeClass(["file-item__string-hashtag", {
            'file-item__string-hashtag-no-empty': _ctx.file.Tags.length !== 0,
          }]),
                  items: _ctx.file.Tags,
                  "show-clear": true,
                  "tooltip-text": _ctx.$t('file_add_tag'),
                  disabled: (props.ruleAccess < 2 && !_unref(isAdmin)) || _ctx.isUploading == true,
                  "selector-enabled": true,
                  "selector-value": selectedTags.value,
                  "entity-type": _unref(TagEntityType).File,
                  "entity-id": _ctx.file.Id,
                  "tag-selector-mode": 'select',
                  "onUpdate:selectorValue": changeTags,
                  onCreateTag: onFileTagCreated,
                  onRemove: removeTag
                }, null, 8, ["class", "items", "tooltip-text", "disabled", "selector-value", "entity-type", "entity-id"]))
              : (_openBlock(), _createBlock(AppHashtag, {
                  key: 1,
                  ref: "hashtagRef",
                  class: _normalizeClass(["file-item__string-hashtag", {
            'file-item__string-hashtag-no-empty': _ctx.file.Tags.length !== 0,
          }]),
                  items: _ctx.file.Tags,
                  "show-clear": true,
                  "selector-enabled": true,
                  "selector-value": selectedTags.value,
                  "entity-type": _unref(TagEntityType).File,
                  "entity-id": _ctx.file.Id,
                  type: "text"
                }, null, 8, ["class", "items", "selector-value", "entity-type", "entity-id"]))
          ], 64))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        _withDirectives(_createElementVNode("span", {
          class: _normalizeClass({
            'file-item__name-disable':
              (!_unref(isAdmin) && !_ctx.file.Editable) || _ctx.isUploading == true,
          }),
          innerHTML: highlightedName.value
        }, null, 10, _hoisted_4), [
          [_vShow, !_unref(isNameEditable) && _unref(searchMode)]
        ]),
        _withDirectives(_createVNode(FileInput, {
          type: "file",
          "model-value": _ctx.file.Name,
          "invalid-values": siblingFileNames.value,
          ref_key: "inputRef",
          ref: inputRef,
          onEnter: renameFile,
          onBlur: blurInput,
          disabled: 
            (!_unref(isAdmin) && (props.ruleAccess == 0 || !_ctx.file.Editable)) ||
            _ctx.isUploading == true
          ,
          blocked: !_unref(isAdmin) && props.ruleAccess !== 2 && !_ctx.file.Editable
        }, null, 8, ["model-value", "invalid-values", "disabled", "blocked"]), [
          [_vShow, _unref(isNameEditable) || !_unref(searchMode)]
        ])
      ]),
      (_ctx.isUploading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(AppIcon, {
              style: {"margin-left":"4px","margin-top":"-4px"},
              name: "clock-upload",
              width: "12px",
              color: "#d2d2d2",
              "hover-color": "#499259",
              cursor: "pointer"
            }),
            _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(percentUploading)) + "%", 1)
          ]))
        : _createCommentVNode("", true),
      (_ctx.isUploading)
        ? (_openBlock(), _createBlock(AppIcon, {
            key: 2,
            style: {"margin-left":"4px","margin-top":"-4px"},
            name: "close",
            width: "12px",
            color: "#d2d2d2",
            "hover-color": "#499259",
            cursor: "pointer",
            onClick: abortUpload
          }))
        : _createCommentVNode("", true)
    ]),
    (
        showJobIcon.value &&
        _ctx.file.JobBindings.length > 1 &&
        !(!_unref(isEditable) || _unref(isNameEditable))
      )
      ? (_openBlock(), _createBlock(AppResizableDropdown, {
          key: 1,
          ref_key: "jobDropdown",
          ref: jobDropdown,
          position: "right",
          resizable: false,
          trigger: "hover",
          "hover-delay": 300,
          "hover-close-delay": 300,
          "close-by-click-outside": false,
          "max-width": 320,
          "max-height": 236,
          "mobile-title": _ctx.$t('choose_job'),
          onOpen: loadBoundJobs
        }, {
          activator: _withCtx(() => [
            _createVNode(AppTooltip, {
              text: _ctx.$t('file_go_to_job')
            }, {
              activator: _withCtx(() => [
                _createVNode(AppIcon, {
                  class: "file-item__option-icons-icon",
                  name: "go-job",
                  width: "16px",
                  color: "#979797",
                  cursor: "pointer",
                  "hover-color": "#499259"
                })
              ]),
              _: 1
            }, 8, ["text"])
          ]),
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(boundJobs.value, (job, index) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: job.Job.Id
                }, [
                  (index !== 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8))
                    : _createCommentVNode("", true),
                  _createVNode(FileJobElement, {
                    job: job.Job,
                    onSelectJob: goToJob
                  }, null, 8, ["job"])
                ], 64))
              }), 128))
            ])
          ]),
          _: 1
        }, 8, ["mobile-title"]))
      : (showJobIcon.value && !(!_unref(isEditable) || _unref(isNameEditable)))
        ? (_openBlock(), _createBlock(AppTooltip, {
            key: 2,
            text: _ctx.$t('file_go_to_job')
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                class: "file-item__option-icons-icon",
                name: "go-job",
                width: "16px",
                color: "#979797",
                cursor: "pointer",
                "hover-color": "#499259",
                onClick: _withModifiers(goToSingleJob, ["stop"])
              })
            ]),
            _: 1
          }, 8, ["text"]))
        : _createCommentVNode("", true),
    (_ctx.fileType == 'default' && !_ctx.isUploading)
      ? (_openBlock(), _createElementBlock("div", {
          key: 3,
          ref_key: "menuAnchorRef",
          ref: menuAnchorRef,
          class: _normalizeClass(["file-item__option-icons", {
        'file-item__option-icons-icon-invisible': (!_unref(isMobile) && !_unref(isEditable)) || _unref(isNameEditable),
      }])
        }, [
          (!_unref(isMobile) && (_unref(isAdmin) || (props.ruleAccess === 2 && _ctx.file.Editable)))
            ? (_openBlock(), _createBlock(AppTooltip, {
                key: 0,
                text: _ctx.$t('file_edit_name')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "file-item__option-icons-icon",
                    name: "edit-pencil",
                    width: "16px",
                    color: "#979797",
                    cursor: "pointer",
                    "hover-color": "#499259",
                    onClick: _withModifiers(editName, ["stop"])
                  })
                ]),
                _: 1
              }, 8, ["text"]))
            : _createCommentVNode("", true),
          (_ctx.file.Editable)
            ? (_openBlock(), _createBlock(FileDropdown, {
                key: 1,
                ref_key: "dropdownMenu",
                ref: dropdownMenu,
                type: "menu",
                onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
              }, {
                default: _withCtx(() => [
                  _createVNode(FileMenu, {
                    elements: menuElementsFile.value,
                    onSelect: _cache[1] || (_cache[1] = 
            (option) => {
              setModalPositionNearDropdown();
              chooseAction(option);
            }
          ),
                    type: "file",
                    "-job-bindings": props.file.JobBindings,
                    onJobsClosed: onJobsClosed,
                    onJobSelected: resetJobNavigationState
                  }, null, 8, ["elements", "-job-bindings"])
                ]),
                _: 1
              }, 512))
            : (_openBlock(), _createBlock(AppTooltip, {
                key: 2,
                text: _ctx.$t('file_view_information')
              }, {
                activator: _withCtx(() => [
                  _createVNode(AppIcon, {
                    class: "folder__option-icons-update",
                    name: "info-circle-outline",
                    width: "16px",
                    color: "#979797",
                    cursor: "pointer",
                    "hover-color": "#499259",
                    onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
                    onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (chooseAction('infoFile')), ["stop"]))
                  })
                ]),
                _: 1
              }, 8, ["text"]))
        ], 2))
      : _createCommentVNode("", true)
  ], 46, _hoisted_1))
}
}

})